/**
 * `/ui-states` — see `web/shapes/ui-states.md`.
 *
 * All nine states of spec 01 §18 **and** every primitive of the class contract on
 * one page, each labelled with the API response or the token that drives it. Three
 * audiences: whoever builds a screen and needs to see the component API rendered
 * rather than described; whoever runs the `docs/UI_PROCESS.md` §4 polish pass and
 * needs every severity in one greyscale screenshot; and the finish reviewer, who
 * needs one page where a drifted button, a second radius or a fourth grey shows up
 * beside the thing it drifted from.
 *
 * **It reads no company data.** Every value below is a literal in this file, which
 * is why it needs no permission beyond a session and cannot disclose anything. It is
 * not one of spec 01 §3's sixteen menu items; it is reached from a link in the
 * sidebar footer.
 *
 * ── The primitives are composed, never re-drawn ─────────────────────────────
 *
 * Every swatch below is the real class or the real component. A reference page that
 * hand-rolled its own button would be showing the reviewer a picture of the design
 * system rather than the design system, and would go on looking right on the day the
 * real one broke.
 */

import { useMemo, useState, type ReactNode } from 'react';

import { useT } from '../i18n';
import { useAuthenticatedUser } from '../auth/SessionProvider';
import { ScreenHeading } from '../shell/ScreenHeading';
import { Badge } from '../states/Badge';
import { ErrorBoundary } from '../states/ErrorBoundary';
import { SEVERITY_ICON, type Severity } from '../states/icons';
import type { MessageKey } from '../i18n/messages';
import {
  AgentOffline,
  CompanyDisabled,
  DataDelayed,
  EmptyState,
  LastSync,
  PartialData,
  PermissionDenied,
  RetryAction,
  Skeleton,
} from '../states';

function Item({
  title,
  driver,
  children,
}: {
  readonly title: string;
  readonly driver: string;
  readonly children: ReactNode;
}): ReactNode {
  const t = useT();
  return (
    <section className="wp-ref__item">
      <h3 className="wp-section__title">{title}</h3>
      <p className="wp-small wp-muted wp-ref__driver">
        {t('states.driver')}: <code>{driver}</code>
      </p>
      {children}
    </section>
  );
}

/**
 * The tenth state, rendered rather than described.
 *
 * The other nine take a prop and draw themselves. This one only exists when a render
 * actually throws, so the sample has to throw for real: pressing the button flips a
 * state, the next render of this child raises, and `ErrorBoundary` catches it exactly
 * as it catches a screen. A mocked-up copy of the error note would go on looking
 * right on the day the boundary stopped working, which is the whole argument this
 * page is built on.
 *
 * Contained on purpose: the boundary is *inside* the reference item, so the throw
 * takes down one cell of the grid and nothing else on the page — including the rest
 * of the reference.
 */
function Thrower({ armed }: { readonly armed: boolean }): ReactNode {
  const t = useT();
  if (armed) throw new Error('ui-states: deliberate render error, the tenth state');
  return <p className="wp-small wp-muted">{t('states.sample.throwOk')}</p>;
}

function RenderErrorSample(): ReactNode {
  const t = useT();
  const [armed, setArmed] = useState(false);
  return (
    <div className="wp-stack">
      {/* `key` on the boundary: "Thử lại" clears the boundary's own state, and this
          clears the reason the child was throwing — without it the retry re-mounts a
          child that is still armed and the note comes straight back. */}
      <ErrorBoundary key={armed ? 'armed' : 'calm'}>
        <Thrower armed={armed} />
      </ErrorBoundary>
      <button
        type="button"
        className="wp-button"
        data-testid="ref-render-error-throw"
        onClick={() => {
          setArmed(true);
        }}
      >
        {t('states.sample.throw')}
      </button>
      <p className="wp-small wp-muted">{t('states.sample.throwHint')}</p>
    </div>
  );
}

const SEVERITIES: readonly Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

const SEVERITY_LABEL: Readonly<Record<Severity, MessageKey>> = {
  critical: 'severity.critical',
  high: 'severity.high',
  medium: 'severity.medium',
  low: 'severity.low',
  info: 'severity.info',
};

const SWATCHES: readonly { readonly modifier: string; readonly label: MessageKey }[] = [
  { modifier: 'bg', label: 'states.prim.colour.bg' },
  { modifier: 'surface', label: 'states.prim.colour.surface' },
  { modifier: 'accent', label: 'states.prim.colour.accent' },
  { modifier: 'cta', label: 'states.prim.colour.cta' },
  { modifier: 'rail', label: 'states.prim.colour.rail' },
  { modifier: 'critical', label: 'severity.critical' },
  { modifier: 'high', label: 'severity.high' },
  { modifier: 'medium', label: 'severity.medium' },
  { modifier: 'low', label: 'severity.low' },
  { modifier: 'info', label: 'severity.info' },
];

export function StatesReferenceScreen(): ReactNode {
  const t = useT();
  const user = useAuthenticatedUser();

  // Relative to now, so the ages on the page read like the ones a real screen shows
  // rather than "3 years ago" a year after this file was written.
  const fixtures = useMemo(() => {
    const now = Date.now();
    return {
      minutesAgo15: new Date(now - 15 * 60_000).toISOString(),
      hoursAgo3: new Date(now - 3 * 3_600_000).toISOString(),
      hoursAgo26: new Date(now - 26 * 3_600_000).toISOString(),
    };
  }, []);

  const noop = (): void => {
    /* the reference page makes no request; the button exists so its focus ring can be checked */
  };

  return (
    <div className="wp-ref">
      <header className="wp-screen-head">
        <ScreenHeading>{t('states.title')}</ScreenHeading>
      </header>
      <p className="wp-muted wp-ref__driver">{t('states.intro')}</p>

      <section aria-labelledby="ref-nine-title">
        <h2 className="wp-section__title" id="ref-nine-title">
          {t('states.nine.title')}
        </h2>

        <div className="wp-ref__grid">
          <Item
            title={t('states.name.skeleton')}
            driver="no response — the in-flight window of GET /auth/me"
          >
            <Skeleton rows={3} testId="ref-skeleton" />
          </Item>

          <Item
            title={t('states.name.empty')}
            driver="200 with data: [] (OffsetListEnvelope, total: 0)"
          >
            <EmptyState
              title={t('states.sample.emptyTitle')}
              description={t('states.sample.emptyBody')}
              testId="ref-empty"
            />
          </Item>

          <Item
            title={t('states.name.permissionDenied')}
            driver="403 WORKFORCE_PERMISSION_DENIED | SCOPE_DENIED | SENSITIVE_DATA_DENIED | ACCESS_REASON_REQUIRED"
          >
            <PermissionDenied
              code="WORKFORCE_SCOPE_DENIED"
              correlationId="3f6b1c90-2d44-4e0a-9c31-b8e2f7a15d04"
              testId="ref-permission-denied"
            />
          </Item>

          <Item
            title={t('states.name.companyDisabled')}
            driver="GET /auth/me → companies[].enabled = false; 403 WORKFORCE_COMPANY_DISABLED at step 2"
          >
            <CompanyDisabled
              reason="company"
              companyName={t('states.sample.company')}
              correlationId="3f6b1c90-2d44-4e0a-9c31-b8e2f7a15d04"
              testId="ref-company-disabled"
            />
          </Item>

          <Item
            title={t('states.name.dataDelayed')}
            driver="AchauSyncState.last_status / TaskSearchResponse.degraded + cache_status; completeness.unsynced_data"
          >
            <DataDelayed
              source="achau"
              since={fixtures.hoursAgo3}
              timezone={user.timezone}
              testId="ref-data-delayed"
            />
          </Item>

          <Item
            title={t('states.name.partialData')}
            driver="AttendanceDay.completeness { score, missing_agent_data, device_count }"
          >
            <PartialData
              excludes={t('states.sample.excludes')}
              score={0.62}
              deviceCount={1}
              testId="ref-partial-data"
            />
          </Item>

          <Item
            title={t('states.name.agentOffline')}
            driver="Device.liveness (computed from last_heartbeat_at, never stored)"
          >
            <AgentOffline
              liveness="offline"
              lastHeartbeatAt={fixtures.hoursAgo26}
              timezone={user.timezone}
              deviceLabel="LAPTOP-KT-07"
              testId="ref-agent-offline"
            />
          </Item>

          <Item
            title={t('states.name.retry')}
            driver="any 5xx, a transport failure, or 429 with details.retry_after_seconds"
          >
            <RetryAction onRetry={noop} retryAfterSeconds={30} testId="ref-retry" />
          </Item>

          <Item
            title={t('states.name.lastSync')}
            driver="max(Device.last_heartbeat_at) and AchauSyncState.last_success_at"
          >
            <LastSync
              agentSyncAt={fixtures.minutesAgo15}
              achauSyncAt={null}
              timezone={user.timezone}
              testId="ref-last-sync"
            />
          </Item>

          {/* The tenth (PRODUCT.md §4, DESIGN.md §14): not a response at all, but a
              render that threw. `states/ErrorBoundary.tsx` is the component; this is
              it, catching a real throw. */}
          <Item
            title={t('states.name.renderError')}
            driver="no response — an exception thrown during render, caught by states/ErrorBoundary"
          >
            <RenderErrorSample />
          </Item>
        </div>
      </section>

      <section aria-labelledby="ref-primitives-title">
        <h2 className="wp-section__title" id="ref-primitives-title">
          {t('states.prim.title')}
        </h2>
        <p className="wp-small wp-muted wp-ref__driver">{t('states.prim.intro')}</p>

        <div className="wp-ref__grid">
          <Item title={t('states.prim.buttons')} driver="primitives.css — .wp-button">
            <div className="wp-ref__rack">
              <button type="button" className="wp-button wp-button--primary" onClick={noop}>
                {t('states.prim.button.primary')}
              </button>
              <button type="button" className="wp-button" onClick={noop}>
                {t('states.prim.button.secondary')}
              </button>
              <button type="button" className="wp-button wp-button--quiet" onClick={noop}>
                {t('states.prim.button.quiet')}
              </button>
              <button type="button" className="wp-button wp-button--danger" onClick={noop}>
                {t('states.prim.button.danger')}
              </button>
              <button type="button" className="wp-button wp-button--sm" onClick={noop}>
                {t('states.prim.button.small')}
              </button>
              <button type="button" className="wp-button" disabled>
                {t('states.prim.button.disabled')}
              </button>
              <button type="button" className="wp-linklike" onClick={noop}>
                {t('states.prim.button.linklike')}
              </button>
            </div>
          </Item>

          <Item title={t('states.prim.fields')} driver="primitives.css — .wp-field">
            <div className="wp-field">
              <label className="wp-field__label" htmlFor="ref-field">
                {t('states.prim.field.label')}
              </label>
              <input
                id="ref-field"
                className="wp-field__input"
                defaultValue="12"
                inputMode="numeric"
                aria-describedby="ref-field-hint"
              />
              <p className="wp-small wp-muted" id="ref-field-hint">
                {t('states.prim.field.hint')}
              </p>
            </div>
            <div className="wp-field wp-field--error">
              <label className="wp-field__label" htmlFor="ref-field-bad">
                {t('states.prim.field.label')}
              </label>
              <input
                id="ref-field-bad"
                className="wp-field__input"
                defaultValue="abc"
                aria-invalid="true"
                aria-describedby="ref-field-bad-error"
              />
              <p className="wp-field__error" id="ref-field-bad-error">
                {t('states.prim.field.error')}
              </p>
            </div>
          </Item>

          <Item title={t('states.prim.badges')} driver="DESIGN.md §4 — token + glyph + text">
            <div className="wp-ref__rack">
              {SEVERITIES.map((severity) => (
                <Badge
                  key={severity}
                  severity={severity}
                  icon={SEVERITY_ICON[severity]}
                  label={t(SEVERITY_LABEL[severity])}
                  testId={`ref-badge-${severity}`}
                />
              ))}
              {/* The sixth chip is not a sixth severity — §4's table is five rows and
                  there is no neutral token. It is the `tone` a status wears when it is
                  the ordinary case, and it stands beside the five here so a reviewer
                  can see it gives up the colour and keeps the glyph and the word. */}
              <Badge
                severity="info"
                tone="neutral"
                icon={SEVERITY_ICON.info}
                label={t('states.prim.badge.neutral')}
                testId="ref-badge-neutral"
              />
            </div>
          </Item>

          <Item title={t('states.prim.toggles')} driver="primitives.css — .wp-toggle-group">
            <div className="wp-toggle-group" role="group" aria-label={t('states.prim.toggles')}>
              <button type="button" className="wp-toggle" aria-pressed onClick={noop}>
                {t('overview.window.today')}
              </button>
              <button type="button" className="wp-toggle" aria-pressed={false} onClick={noop}>
                {t('overview.window.7d')}
              </button>
              <button type="button" className="wp-toggle" aria-pressed={false} onClick={noop}>
                {t('overview.window.30d')}
              </button>
            </div>
          </Item>

          <Item title={t('states.prim.rows')} driver="primitives.css — .wp-rows / .wp-row">
            <ul className="wp-rows">
              <li className="wp-row">
                <span>{t('states.prim.row.plain')}</span>
                <span className="wp-num">12</span>
              </li>
              <li className="wp-row wp-row--selected">
                <span>{t('states.prim.row.selected')}</span>
                <span className="wp-num">3</span>
              </li>
            </ul>
          </Item>

          <Item title={t('states.prim.table')} driver="primitives.css — .wp-table, 36px rows">
            <div className="wp-table-scroll">
              <table className="wp-table wp-table--fluid">
                <thead>
                  <tr>
                    <th scope="col">{t('states.prim.table.col.name')}</th>
                    <th scope="col" className="wp-num">
                      {t('states.prim.table.col.value')}
                    </th>
                    <th scope="col">{t('states.prim.table.col.when')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Nguyễn Văn A</th>
                    <td className="wp-num">7 g 40 ph</td>
                    <td>08:12</td>
                  </tr>
                  <tr>
                    <th scope="row">Trần Thị B</th>
                    <td className="wp-num">4 g 05 ph</td>
                    <td>09:41</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Item>

          <Item title={t('states.prim.tile')} driver="primitives.css — .wp-tile, 26px figure">
            <section className="wp-tile">
              <h4 className="wp-tile__title">{t('states.prim.tile.title')}</h4>
              <div className="wp-tile__figure">
                <span className="wp-figure">58%</span>
              </div>
              <p className="wp-small wp-muted">{t('states.prim.tile.detail')}</p>
            </section>
          </Item>

          <Item title={t('states.prim.mono')} driver="base.css — .wp-mono / .wp-code / .wp-flag">
            <div className="wp-ref__rack">
              <span className="wp-flag">{t('states.prim.flag')}</span>
              <code className="wp-mono">4f2c8e10-9a3b-4c55-8d21-77e0b6f1c934</code>
            </div>
            <pre className="wp-code">GET /api/v1/alerts?status=open&amp;limit=50</pre>
          </Item>

          <Item title={t('states.prim.type')} driver="DESIGN.md §6 — two families, six steps">
            <dl className="wp-ref__type">
              <dt>{t('states.prim.type.display')}</dt>
              <dd className="wp-ref__display">1.248</dd>
              <dt>{t('states.prim.type.headline')}</dt>
              <dd className="wp-ref__headline">{t('states.prim.type.sample')}</dd>
              <dt>{t('states.prim.type.title')}</dt>
              <dd className="wp-ref__title">{t('states.prim.type.sample')}</dd>
              <dt>{t('states.prim.type.body')}</dt>
              <dd>{t('states.prim.type.sample')}</dd>
              <dt>{t('states.prim.type.small')}</dt>
              <dd className="wp-small wp-muted">{t('states.prim.type.sample')}</dd>
              <dt>{t('states.prim.type.label')}</dt>
              <dd className="wp-label">{t('states.prim.type.sample')}</dd>
            </dl>
          </Item>

          <Item title={t('states.prim.colour')} driver="tokens.css — DESIGN.md §3 and §5">
            <ul className="wp-ref__swatches">
              {SWATCHES.map((swatch) => (
                <li className="wp-ref__swatch" key={swatch.modifier}>
                  <span className={`wp-ref__chip wp-ref__chip--${swatch.modifier}`} />
                  <span className="wp-small wp-muted">{t(swatch.label)}</span>
                </li>
              ))}
            </ul>
          </Item>
        </div>
      </section>
    </div>
  );
}
